import { useSelector } from "react-redux";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import ArrowUpRed from "@Icons/ArrowUpRed";
import ArrowDownGreen from "@Icons/ArrowDownGreen";
import InfoIcon from "@Icons/InfoIcon";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import getCurrencySymbol from "helpers/getCurrencySymbol";
import { AppState } from "@Store/index";
import { CostOfLivingData } from "../types";
import { resolveCopy, renderBoldText } from "../utils";
import NeighbourhoodCard from "./NeighbourhoodCard";
import CARD_ICONS from "./cardAssets";
import styles from "../neighbourhoodSection.module.scss";

const KEY_PREFIX = "propertypage.components.neighbourhood.costOfLiving";

type CostOfLivingTabProps = {
  data: CostOfLivingData;
};

const CostOfLivingTab = ({ data }: CostOfLivingTabProps) => {
  const { t } = useTranslationHook();

  const pricing = useSelector((state: AppState) => state.propertyPageData?.data?.pricing);

  // Average rent for the region, from the Price Trend entry for the current month
  // and year (P1D-5482 FR-5). `city_avg_cost` is a weekly figure, so a monthly-priced
  // property shows it multiplied by four, exactly as the Price Trends section does.
  const cityAvgCost = useSelector((state: AppState) => {
    const trends = state.propertyPageData?.priceTrendsData?.trends;
    if (!Array.isArray(trends)) return null;
    const now = new Date();
    const currentTrend = trends.find(
      (trend: any) =>
        trend?.year_of_date === now.getFullYear() && trend?.month_of_date === now.getMonth() + 1,
    );
    const raw = currentTrend?.city_avg_cost;
    const num = typeof raw === "string" ? parseFloat(raw) : raw;
    return typeof num === "number" && Number.isFinite(num) && num > 0 ? num : null;
  });

  const isMonthly = pricing?.duration === "monthly";
  // No average rent → the footer strip and its divider are not rendered at all.
  const averageRent =
    cityAvgCost !== null
      ? `${getCurrencySymbol(pricing?.currency)}${Math.round(
          isMonthly ? cityAvgCost * 4 : cityAvgCost,
        ).toLocaleString("en")} ${resolveCopy(
          t,
          isMonthly ? `${KEY_PREFIX}.footer.perMonth` : `${KEY_PREFIX}.footer.perWeek`,
        )}`
      : null;

  const cardSuffix = data.direction
    ? resolveCopy(t, `${KEY_PREFIX}.hero.suffix.${data.direction}`)
    : undefined;
  const cardStat =
    data.percentageDiff && cardSuffix
      ? `${data.percentageDiff} ${cardSuffix}`
      : data.percentageDiff;
  const cardSubtext = resolveCopy(t, data.comparisonLabelKey, data.comparisonLabelParams);
  const source = resolveCopy(t, data.sourceKey);
  const sourceLabel = resolveCopy(t, "propertypage.components.neighbourhood.source.label");

  // Rows whose copy resolves to nothing are dropped along with their divider
  // (P1D-5482): a missing data point leaves no gap and no stray rule.
  const renderedFacts = data.facts
    .map((fact) => ({ fact, statement: resolveCopy(t, fact.key, fact.params) }))
    .filter(({ statement }) => !!statement);

  return (
    <div className={styles.contentArea}>
      <NeighbourhoodCard
        image={data.heroImage}
        icon={CARD_ICONS.costOfLiving}
        label={resolveCopy(t, `${KEY_PREFIX}.card.label`)}
        stat={cardStat}
        subtext={cardSubtext}
        alt="Cost of living neighbourhood view"
        footer={
          averageRent ? (
            <>
              {resolveCopy(t, `${KEY_PREFIX}.footer.averageRent`)} &middot;{" "}
              <strong>{averageRent}</strong>
            </>
          ) : undefined
        }
      />
      <div className={styles.detailsSection}>
        <div>
          {renderedFacts.map(({ fact, statement }, index) => (
            <div key={fact.key}>
              <div
                className={formatClassNames(styles.tableRow, index === 0 && styles.tableRowFirst)}
              >
                <span className={styles.tableText}>{renderBoldText(statement)}</span>
                <span className={styles.tableIcon} aria-hidden="true">
                  {fact.trend === "up" && <ArrowUpRed />}
                  {fact.trend === "down" && <ArrowDownGreen />}
                </span>
              </div>
              {index < renderedFacts.length - 1 && <div className={styles.tableDivider} />}
            </div>
          ))}
        </div>
        <p className={styles.sourceText}>
          <span className={styles.sourceIcon} aria-hidden="true">
            <InfoIcon width="14" height="14" fill="#6b7280" />
          </span>
          {sourceLabel} : {source}
        </p>
      </div>
    </div>
  );
};

export default wrapperHOC(CostOfLivingTab, {
  componentName: "CostOfLivingTab",
  showForChina: false,
  showForWhiteLable: false,
});
